import React, { useState, useEffect } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Divider from '@mui/material/Divider'
import InputAdornment from '@mui/material/InputAdornment'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import CloseIcon from '@mui/icons-material/Close'
import SettingsIcon from '@mui/icons-material/Settings'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import BlockIcon from '@mui/icons-material/Block'
import FolderIcon from '@mui/icons-material/Folder'
import SaveIcon from '@mui/icons-material/Save'
import FilterCard from './FilterCard'
import PathsSection from './PathsSection'
import ForwardingRules from './ForwardingRules'

const DOC_TYPES = ['AMA', 'MIR', 'NTV', 'PIC', 'SBR']

const DUPLICATE_ACTIONS = [
  { value: 'skip', label: 'Ignora', helper: 'Nessun record salvato: il file va solo nel backup Duplicati/.' },
  { value: 'save', label: 'Salva senza spedire', helper: 'Record in stato "Duplicato", spedibile a mano dalla pagina Processi.' },
  { value: 'process', label: 'Processa e spedisci', helper: 'Pipeline completa (tokenizzazione + FTP). Il record resta marcato come duplicato.' }
]

// Le config non ancora migrate hanno solo il vecchio flag booleano
const resolveDuplicateAction = (filter) =>
  filter?.duplicateAction || (filter?.saveDuplicateProcess ? 'save' : 'skip')

const makeRegexString = (input) => {
  if (typeof input === 'string') {
    if (input.length === 0 || (input[0] === '/' && input[input.length - 1] === '/')) return input
    return '/' + input + '/'
  }
  return input?.regex || input?.flags
    ? '/' + (input?.regex || '') + '/' + (input?.flags || '')
    : ''
}

const checkRegex = (regexInput, value) => {
  if (!regexInput || regexInput === '//') return false

  const [pattern, flags] = typeof regexInput === 'string'
    ? (regexInput[0] === '/' ? regexInput.slice(1, -1).split('/') : [regexInput, ''])
    : [regexInput.regex, regexInput.flags]

  return new RegExp(pattern, flags).test(value)
}

/** Match estensione o nome file con pattern (supporta * in qualsiasi posizione, come nel client). */
const extensionMatchesPattern = (extension, pattern) => {
  if (!pattern || pattern === '') return false
  if (!pattern.includes('*')) return extension === pattern
  const escaped = pattern
    .replace(/\*/g, '\u0001')
    .replace(/[.+?^${}()|[\]\\]/g, '\\$&')
    .replace(/\u0001/g, '.*')
  try {
    return new RegExp('^' + escaped + '$').test(extension)
  } catch {
    return extension === pattern
  }
}

const matchesLocalSilentBlacklist = (fileName, entries) => {
  if (!Array.isArray(entries) || entries.length === 0) return false
  for (const e of entries) {
    const pat = typeof e === 'string' ? e.trim() : (e && e.regex != null ? String(e.regex).trim() : '')
    if (!pat) continue
    if (extensionMatchesPattern(fileName, pat)) return true
  }
  return false
}

export const ConfigDialog = ({ open, onClose, onConfirmSave, configToEdit: config, onSave }) => {
  const [configToEdit, setConfigToEdit] = useState(null)
  const [paths, setPaths] = useState([])
  const [testInput, setTestInput] = useState('New Text Document.txt')
  const [testIsPassed, setTestIsPassed] = useState(false)
  const [testSilentBlacklisted, setTestSilentBlacklisted] = useState(false)
  const [selectedPath, setSelectedPath] = useState(null)
  const [regexValue, setRegexValue] = useState('')
  const [forwardingRules, setForwardingRules] = useState([])

  const runFiltersCheck = (value, pathIndex, cfg) => {
    const filters = cfg.filters
    const current = filters[pathIndex]
    if (!current) return false

    const { fileNameFiltersToExclude, fileExtensionsToInclude, inclusionRegex, localSilentBlacklist } = current
    if (matchesLocalSilentBlacklist(value, localSilentBlacklist)) {
      setTestInput(value)
      setTestSilentBlacklisted(true)
      setTestIsPassed(false)
      return false
    }
    setTestSilentBlacklisted(false)

    const nameIsValid = (fileNameFiltersToExclude || []).every(n => !n || !value.includes(n))
    const ext = (value.split('.')[1] || '').trim()
    const extIsValid = (fileExtensionsToInclude || []).some(pat => extensionMatchesPattern(ext, pat))
    const regexValid = checkRegex(inclusionRegex, value)
    const passed = (inclusionRegex === undefined || regexValid) || (nameIsValid && extIsValid)

    setTestInput(value)
    setTestIsPassed(passed)
    return passed
  }

  useEffect(() => {
    if (!config || !open) return

    const initialPaths = config.filters?.map((item, index) => ({ path: item.path, index })) || []

    setConfigToEdit(config)
    setPaths(initialPaths)
    setSelectedPath(initialPaths[0] || null)
    setForwardingRules(config.filters?.[0]?.forwardingRules || [])
    setRegexValue(makeRegexString(config.filters?.[0]?.inclusionRegex))
    setTestInput('New Text Document.txt')
    setTestSilentBlacklisted(false)
    runFiltersCheck('New Text Document.txt', 0, config)
  }, [config, open])

  if (!configToEdit || !selectedPath) return null

  const makeNewConfig = (pathIndex, filterKey, newValue, dataKeyInArray, workingConfig) => {
    if ((!newValue || newValue[newValue.length - 1] === '') && newValue[newValue.length - 2] === '') return

    const cfg = workingConfig || configToEdit

    if (dataKeyInArray && ['fileExtensionsToInclude', 'fileNameFiltersToExclude'].includes(filterKey)) {
      newValue = newValue.map((value, i) => (i === dataKeyInArray ? value.replaceAll('.', '') : value))
    }

    if (filterKey === 'docType') {
      const samePath = cfg.filters.filter((f, i) => f.path === cfg.filters[pathIndex].path && i !== dataKeyInArray)
      if (samePath.some(f => f.docType === newValue)) {
        window.growl.show({ severity: 'error', summary: 'Error', detail: 'Duplicate doc type on same path.' })
        return
      }
    }

    if (filterKey === 'inclusionRegex') {
      setRegexValue(typeof newValue === 'string'
        ? newValue
        : { regex: newValue?.regex || '', flags: newValue?.flags || '' })
    }

    const newConfig = {
      ...cfg,
      filters: cfg.filters.map((f, i) => (i === pathIndex ? { ...f, [filterKey]: newValue } : f))
    }

    setConfigToEdit(newConfig)
    onSave(newConfig)
    runFiltersCheck(testInput, selectedPath?.index, newConfig)

    return newConfig
  }

  const checkFiltersOnInputInternal = (value, pathIndex, newCfg) => {
    const cfg = newCfg || configToEdit
    const index = pathIndex ?? selectedPath?.index
    return runFiltersCheck(value, index, cfg)
  }

  const duplicateAction = resolveDuplicateAction(configToEdit?.filters[selectedPath?.index])
  const selectedDocType = configToEdit?.filters[selectedPath?.index]?.docType
  const showForwarding = ['AMA', 'MIR', 'SBR'].includes(selectedDocType)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='xl'
      fullWidth
      aria-labelledby='config-dialog-title'
      PaperProps={{ sx: { height: '90vh', maxHeight: '90vh' } }}
    >
      <DialogTitle
        id='config-dialog-title'
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Stack direction='row' alignItems='center' spacing={1.5}>
          <SettingsIcon color='primary' />
          <Typography variant='h6' component='span' fontWeight={600}>
            Modifica Configurazione
          </Typography>
          <Chip label={configToEdit?.name || ''} size='small' color='primary' variant='outlined' />
        </Stack>
        <IconButton onClick={onClose} size='small' aria-label='Chiudi'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* LEFT PANEL - Paths */}
          <Paper
            elevation={0}
            sx={{
              width: 650,
              minWidth: 340,
              borderRight: '1px solid',
              borderColor: 'divider',
              p: 2,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography variant='subtitle2' sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>
              Percorsi Monitorati
            </Typography>
            <PathsSection
              saveConfigChanges={onSave}
              paths={paths}
              setPaths={setPaths}
              makeNewConfig={makeNewConfig}
              configToEdit={configToEdit}
              setConfigToEdit={setConfigToEdit}
              selectedPath={selectedPath}
              setSelectedPath={setSelectedPath}
              checkFiltersOnInput={checkFiltersOnInputInternal}
              setRegexValue={setRegexValue}
              testInput={testInput}
              makeRegexString={makeRegexString}
              setForwardingRules={setForwardingRules}
            />
          </Paper>

          {/* RIGHT PANEL - Filters & Settings */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* Selected Path Header */}
            {selectedPath?.path && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}
              >
                <FolderIcon sx={{ fontSize: 20 }} />
                <Box>
                  <Typography variant='caption' sx={{ opacity: 0.8 }}>
                    Configurazione per:
                  </Typography>
                  <Typography variant='body2' fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                    {selectedPath.path}
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* Filter Cards */}
            <Stack direction='row' spacing={1.5}>
              <FilterCard
                data={configToEdit?.filters[selectedPath?.index]?.fileNameFiltersToExclude}
                configToEdit={configToEdit}
                makeNewConfig={makeNewConfig}
                selectedPath={selectedPath}
                title='Escludi per Nome'
                paths={paths}
                filterKey='fileNameFiltersToExclude'
                checkFiltersOnInput={checkFiltersOnInputInternal}
              />
              <FilterCard
                data={configToEdit?.filters[selectedPath?.index]?.fileExtensionsToInclude}
                configToEdit={configToEdit}
                makeNewConfig={makeNewConfig}
                selectedPath={selectedPath}
                title='Estensioni Consentite'
                paths={paths}
                filterKey='fileExtensionsToInclude'
                checkFiltersOnInput={checkFiltersOnInputInternal}
                helperText='Supporto al carattere * (es. CN* → file.CN, file.CN1, file.CN2...).'
              />
            </Stack>

          
            {/* Regex + DocType + Backup */}
            <Paper variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
              <Stack spacing={2}>
                <TextField
                  label='Regex di Inclusione'
                  value={regexValue}
                  onChange={(e) => {
                    const val = e.target.value
                    setRegexValue(val)
                    makeNewConfig(selectedPath.index, 'inclusionRegex', val)
                  }}
                  disabled={!paths[selectedPath?.index]?.path}
                  size='small'
                  fullWidth
                />

                <FormControl size='small' fullWidth disabled={!paths[selectedPath?.index]?.path}>
                  <InputLabel>Tipo Documento</InputLabel>
                  <Select
                    value={selectedDocType || ''}
                    onChange={(e) => makeNewConfig(selectedPath.index, 'docType', e.target.value)}
                    label='Tipo Documento'
                  >
                    {DOC_TYPES.map(dt => <MenuItem key={dt} value={dt}>{dt}</MenuItem>)}
                  </Select>
                </FormControl>

                <TextField
                  label='Path di Backup'
                  value={configToEdit?.filters[selectedPath?.index]?.backupPath || ''}
                  onChange={(e) => makeNewConfig(selectedPath.index, 'backupPath', e.target.value)}
                  placeholder='Inserisci il percorso di backup'
                  disabled={!paths[selectedPath?.index]?.path}
                  size='small'
                  fullWidth
                />

                <FormControl size='small' fullWidth disabled={!paths[selectedPath?.index]?.path}>
                  <InputLabel id='duplicate-action-label'>Processi Duplicati</InputLabel>
                  <Select
                    labelId='duplicate-action-label'
                    value={duplicateAction}
                    onChange={(e) => {
                      const value = e.target.value
                      // Dual-write: i client non ancora aggiornati leggono solo saveDuplicateProcess.
                      // Senza questo passerebbero a 'skip' silenzioso durante il rollout.
                      const updated = makeNewConfig(selectedPath.index, 'duplicateAction', value)
                      makeNewConfig(selectedPath.index, 'saveDuplicateProcess', value !== 'skip', undefined, updated)
                    }}
                    label='Processi Duplicati'
                  >
                    {DUPLICATE_ACTIONS.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {DUPLICATE_ACTIONS.find(a => a.value === duplicateAction)?.helper}
                  </FormHelperText>
                </FormControl>
              </Stack>
            </Paper>

            <FilterCard
              data={configToEdit?.filters[selectedPath?.index]?.localSilentBlacklist}
              configToEdit={configToEdit}
              makeNewConfig={makeNewConfig}
              selectedPath={selectedPath}
              title='Blacklist locale (silenziosa)'
              paths={paths}
              filterKey='localSilentBlacklist'
              checkFiltersOnInput={checkFiltersOnInputInternal}
              helperText='Nome file esatto o * (es. *.tmp, report_*, prova.db). I file che corrispondono verranno ignorati e non genereranno alcun log.'
            />


            {/* Test Input */}
            <Paper variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
                Test Filtri
              </Typography>
              <TextField
                label='Nome file da testare'
                value={testInput}
                onChange={(e) => checkFiltersOnInputInternal(e.target.value)}
                size='small'
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position='end'>
                        {testSilentBlacklisted
                          ? <BlockIcon sx={{ color: 'warning.main' }} />
                          : testIsPassed
                            ? <CheckCircleIcon sx={{ color: 'success.main' }} />
                            : <CancelIcon sx={{ color: 'error.main' }} />}
                      </InputAdornment>
                    )
                  }
                }}
              />
              <Typography
                variant='caption'
                sx={{ mt: 0.5, display: 'block' }}
                color={testSilentBlacklisted ? 'warning.main' : testIsPassed ? 'success.main' : 'error.main'}
              >
                {testSilentBlacklisted
                  ? 'Il file corrisponde alla blacklist locale silenziosa: nel client verrà ignorato senza log.'
                  : testIsPassed
                    ? 'Il file supera i filtri configurati'
                    : 'Il file non supera i filtri configurati'}
              </Typography>
            </Paper>

            {/* Forwarding Rules */}
            {showForwarding && (
              <Paper variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant='subtitle1' sx={{ mb: 1.5, fontWeight: 600 }}>
                  Impostazioni Reinoltro
                </Typography>
                <ForwardingRules rulesData={forwardingRules} makeNewConfig={makeNewConfig} selectedPath={selectedPath} />
              </Paper>
            )}
          </Box>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          onClick={onClose}
          variant='outlined'
          size='small'
          sx={{ textTransform: 'none', fontWeight: 500 }}
        >
          Annulla
        </Button>
        <Button
          onClick={onConfirmSave}
          variant='contained'
          size='small'
          startIcon={<SaveIcon />}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Salva
        </Button>
      </DialogActions>
    </Dialog>
  )
}
